import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Dropdown as AntDropdown, Form } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import FilterDropdownHeader from 'components/lib/FilterDropdownHeader';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { DEFAULT_FILTERS, VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';
import { ViewFilters } from 'components/View/types';

import CompareCommunitiesFilterContent from './CompareCommunitiesFilterContent';
import {
  CompareCommunitiesFilterContext,
  CompareCommunitiesFilterFormValues,
  defaultCompareCommunitiesFilterValues,
} from './CompareCommunitiesFilterContext';

const StyledDropdown = styled(AntDropdown)`
  font-family: var(--font-regular);
  font-size: 12px;
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 12px;
  cursor: pointer;

  color: var(--deep-blue);
  background-color: var(--lighter-gray);

  &.active {
    color: var(--dark-blue);
    background-color: var(--faded-blue);
  }
    &.focused {
    color: var(--dark-blue);
    background-color: var(--faded-blue);
    border: 1px solid var(--button-blue);
  }
`;

const TriggerLabel = styled.span`
  font-family: var(--font-regular);
`;

const BoldLabel = styled.span`
  font-family: var(--font-bold);
  margin-right: 4px;
`;

const TriggerIcon = styled.span`
  display: flex;
  align-items: center;
  margin-left: 6px;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 322px;
  background-color: white;
  border: 1px solid var(--line-gray);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
`;

const CompareCommunitiesFilter = () => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<CompareCommunitiesFilterFormValues>();
  const { viewFilters, setViewFilters, isViewCMA } = useCommunityViewContext();
  const typedFilters = viewFilters as ViewFilters | undefined;

  const initialValues = useMemo(() => {
    if (!typedFilters) {
      return defaultCompareCommunitiesFilterValues;
    }

    const valueDisplay = isViewCMA
      ? (typedFilters as any)?.viewOptions?.valueDisplay
      : typedFilters.valueDisplay;
    const comparisonEnabled = isViewCMA
      ? valueDisplay !== VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY
      : (typedFilters.comparisonTypeEnabled ?? true);

    return {
      comparisonTypeEnabled: comparisonEnabled,
      valueDisplay: !comparisonEnabled
        ? VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY
        : valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES
          ? VALUE_DIFFERENCE_DISPLAY.PERCENTAGES
          : VALUE_DIFFERENCE_DISPLAY.AMOUNTS,
    };
  }, [typedFilters, isViewCMA]);


  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const handleReset = useCallback(() => {
    form.setFieldsValue(defaultCompareCommunitiesFilterValues);
  }, [form]);

  const handleApply = useCallback(
    (values: CompareCommunitiesFilterFormValues) => {
      setViewFilters((prevState: ViewFilters = DEFAULT_FILTERS) => ({
        ...prevState,
        ...(!isViewCMA
          ? {
            comparisonTypeEnabled: values.comparisonTypeEnabled ?? false,
            valueDisplay: values.comparisonTypeEnabled
              ? values.valueDisplay ?? VALUE_DIFFERENCE_DISPLAY.AMOUNTS
              : VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY,
          }
          : {}),
        ...(isViewCMA
          ? {
            viewOptions: {
              ...(prevState as any)?.viewOptions,
              valueDisplay: values.comparisonTypeEnabled
                ? values.valueDisplay ?? VALUE_DIFFERENCE_DISPLAY.AMOUNTS
                : VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY,
            },
          }
          : {}),
      }));
      setOpen(false);
    },
    [setViewFilters, isViewCMA]
  );

  const handleValueChange = (changedValues: any, allValues: any) => {

    if ('comparisonTypeEnabled' in changedValues) {
      const enabled = changedValues.comparisonTypeEnabled;
      if (enabled) {
        if (allValues.valueDisplay !== VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
          form.setFieldsValue({ valueDisplay: VALUE_DIFFERENCE_DISPLAY.AMOUNTS });
        }
      } else {
        form.setFieldsValue({ valueDisplay: VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY });
      }
    }
  };
  const isActive = isViewCMA
    ? (typedFilters as any)?.viewOptions?.valueDisplay !== VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY
    : Boolean(typedFilters?.comparisonTypeEnabled);

  const triggerLabel =
    !isActive
      ? 'Compare'
      : 'My community vs. competitors';

  return (
    <StyledDropdown
      trigger={['click']}
      open={open}
      onOpenChange={(visible) => setOpen(visible)}
      placement="bottomLeft"
      dropdownRender={() => (
        <DropdownContainer onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
          <CompareCommunitiesFilterContext.Provider
            value={{
              form,
            }}
          >
            <Form form={form} onFinish={handleApply} initialValues={defaultCompareCommunitiesFilterValues} onValuesChange={handleValueChange}>
              <FilterDropdownHeader onReset={handleReset} />
              <CompareCommunitiesFilterContent />
            </Form>
          </CompareCommunitiesFilterContext.Provider>
        </DropdownContainer>
      )}
    >
      <TriggerButton
        type="button"
        className={classNames({ active: isActive, focused: open })}
        onClick={(event) => event.preventDefault()}
        data-test-id="compare-filter"
      >
        <span>
          <BoldLabel>{isActive ? 'Compare:' : ''}</BoldLabel>
          <TriggerLabel>{triggerLabel}</TriggerLabel>
        </span>
        <TriggerIcon>
          <img src="/icons/dropdown-caret-down.svg" width={10} height={10} alt="toggle" />
        </TriggerIcon>
      </TriggerButton>
    </StyledDropdown>
  );
};

export default CompareCommunitiesFilter;
